import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Routes as ReactRoutes, useLocation, Navigate } from "react-router-dom";
import Spinner from 'Common/Globle/Spinner';
import { useAuth } from 'Context/AuthContext';

// Lazy-load each page — only downloaded when the user navigates to that route
const Home = lazy(() => import('Frontend/HomePage/Home'));
const RoomsPage = lazy(() => import('Frontend/RoomsPage/RoomsPage'));
const RoomDetailsPage = lazy(() => import('Frontend/RoomDetailsPage/RoomDetailsPage'));
const ContectPage = lazy(() => import('Frontend/ContectPage/ContectPage'));
const AboutPage = lazy(() => import('Frontend/AboutPage/AboutPage'));
const BlogListPage = lazy(() => import('Frontend/BlogPages/BlogListPage'));
const BlogDetailsPage = lazy(() => import('Frontend/BlogPages/BlogDetailsPage'));
const AccountPage = lazy(() => import('Frontend/AuthPages/AccountPage'));
const PackageListPage = lazy(() => import('Frontend/EventPackages/PackageListPage'));
const PackageDetailsPage = lazy(() => import('Frontend/EventPackages/PackageDetailsPage'));
const CheckoutPage = lazy(() => import('Frontend/BookingFlow/CheckoutPage'));
const SuccessPage = lazy(() => import('Frontend/BookingFlow/SuccessPage'));
const DynamicPage = lazy(() => import('Frontend/DynamicPage/DynamicPage'));
const HistoryPage = lazy(() => import('Frontend/history/history'));

// Scrolls to top on every route change
const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [pathname]);
    return null;
};

// Redirects unauthenticated users back to /rooms with a ?login=1 flag
// so the navbar or any listener can auto-open the login modal
const ProtectedRoute = ({ children }) => {
    const { isLoggedIn, authReady } = useAuth();
    const location = useLocation();

    // Wait until auth state is confirmed from localStorage before deciding
    if (!authReady) return <Spinner isSpinner={true} />;

    if (!isLoggedIn) {
        // Preserve the intended destination so we can redirect back after login
        return (
            <Navigate
                to={`/rooms?login=1&redirect=${encodeURIComponent(location.pathname + location.search)}`}
                replace
            />
        );
    }

    return children;
};

const FrontSideRoutes = () => {
    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<Spinner isSpinner={true} />}>
                <ReactRoutes>
                    <Route path="/" element={<Home />} />
                    <Route path="/rooms" element={<RoomsPage />} />
                    <Route path="/rooms/:roomId" element={<RoomDetailsPage />} />
                    <Route path="/contact" element={<ContectPage />} />
                    <Route path="/history" element={<HistoryPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/blog" element={<BlogListPage />} />
                    <Route path="/blog/:slug" element={<BlogDetailsPage />} />

                    {/* Dynamic Pages (Replacing hardcoded privacy/terms eventually) */}
                    <Route path="/page/:slug" element={<DynamicPage />} />

                    <Route path="/event-packages" element={<PackageListPage />} />
                    <Route path="/event-packages/:id" element={<PackageDetailsPage />} />

                    {/* Protected — login required */}
                    <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
                    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
                    <Route path="/booking-success" element={<ProtectedRoute><SuccessPage /></ProtectedRoute>} />
                </ReactRoutes>
            </Suspense>
        </>
    );
};

export default FrontSideRoutes;
